// 制御メッセージ (protocol/src/control.rs の写し) と枠付け。
//
// 各メッセージは CBOR にして、4 バイトのビッグエンディアン長さを前置きする。
// 列挙型は {"type": "join", ...} の形 (内部タグ)。ここは protocol/ と 1 対 1 に保つ。

import { decode as cborDecode, encode as cborEncode, type CborValue } from "./cbor.ts";

/**
 * protocol/src/lib.rs の PROTOCOL_VERSION。core の WASM からも取れる (protocol_version())。
 *
 * - 2 = 音声プロファイル・可変長ペイロード・部屋一覧を追加 (p0005)
 * - 3 = 合成音声での参加者ごとの音量 `set_mix_gain` を追加 (#58・2026-10-07)
 * - 4 = **サーバから見た上りの状態 `uplink_report`** を追加し、音声フレームの種別 2
 *   (G.729 Annex B の無音記述) を削除 (#60・2026-10-08)
 * - 5 = **チャンネルを作る専用の要求** `create_channel` / `channel_created` (ADR-0022) と
 *   **通話中の受信統計** `stats` (ADR-0012) を追加 (2026-10-21)。種別が増えたので上げた
 *   (項目の追加だけなら上げなくてよい。ADR-0019 §5-2)
 *
 * **ここを Rust と揃え忘れると入室できない**（サーバが `unsupported_version` で断る）。
 * #58 で Rust を 3 に上げたときここが 2 のまま残っていたのを 2026-10-08 に直した。
 * **ずれは `test/protocol-version.test.ts` が止める**（`protocol/src/lib.rs` を読んで突き合わせる）。
 */
export const PROTOCOL_VERSION = 5;

/** 長さ前置きのバイト数。 */
export const LENGTH_PREFIX_LEN = 4;
/** 1 メッセージの CBOR 部分の上限 (64 KiB)。超えたら接続を切ってよい。 */
export const MAX_MESSAGE_LEN = 64 * 1024;

export type Topology = "sfu" | "mcu";

/**
 * 音声コーデック。**Opus だけ**（2026-10-07 の ADR-0006 で決定。G.729 は廃止）。
 *
 * 1 択だが型として残してある — サーバ (protocol/src/profile.rs) の `Codec` も enum のままで、
 * 将来 G.711 などを足す余地を残している。
 */
export type Codec = "opus";

/**
 * 部屋ごとの音声の設定 (protocol/src/profile.rs の AudioProfile の写し)。
 * 入室応答で受け取り、これを見て音声パイプラインを組み立てる。
 */
export interface AudioProfile {
  codec: Codec;
  /** 符号化する時点の標本化周波数 (Hz)。マイクの 48 kHz とは別。 */
  sample_rate_hz: number;
  /** 1 フレームの長さ (ms)。**1 パケット = 1 フレームなので送信間隔と同じ。** */
  frame_ms: number;
  bitrate_bps: number;
  vbr: boolean;
  dtx: boolean;
  fec: boolean;
  /** ジッタバッファの指定 (ms)。実際の深さはパケット間隔で切り上げる。 */
  jitter_ms: number;
}

/** 入室前に見せる部屋の案内 (protocol/src/control.rs の RoomInfo の写し)。 */
export interface RoomInfo {
  /**
   * **いま入っている人の表示名**（ADR-0019 §1-9・§1-10）。**省略可能なのでバージョンは上がらない。**
   *
   * 入室前に「誰が居るか」を見せるために要る（呼び出しを作らない代わり）。
   * **サーバ側はバージョン 5 のパッチで入る**（板 #199）ので、それまでは届かない。
   */
  participant_names?: string[];
  /** join で指定する識別子 (例 "opus-quality")。 */
  id: string;
  /** 画面に出す名前 (例 "Opus 音質重視")。 */
  display_name: string;
  topology: Topology;
  audio_profile: AudioProfile;
  participants: number;
  max_participants: number;
}

/**
 * 1 パケットが運ぶ音の長さ (ms) = 送信間隔。
 *
 * **1 パケット = 1 フレームなので `frame_ms` と同じ値。** 関数を残しているのは「送信間隔」
 * として読む場所があるため（遅延の式・ジッタバッファの計算）。
 * 複数フレーム詰め (`frames_per_packet`) は 2026-10-08 に撤去した (ADR-0006)。
 */
export function packetMs(p: AudioProfile): number {
  return p.frame_ms;
}

/** ジッタバッファの実際の深さ (ms)。パケット間隔より短くはしない。 */
export function jitterDepthMs(p: AudioProfile): number {
  return Math.max(p.jitter_ms, packetMs(p));
}

export interface Participant {
  /**
   * **この人がミュートしているか**（S1）。**省略可能**なのでバージョンは上がらない。
   *
   * `undefined` は「申告が無い」（まだ音声パケットが届いていない）。サーバが音声フレームの
   * 旗を見て埋めるので、クライアントは申告しない。画面では印を出すだけ（画面仕様 §3）。
   */
  muted?: boolean;
  id: number;
  name: string;
}

export type ClientMessage =
  | {
      type: "join";
      protocol_version: number;
      /** `グループ/管理ID`（動的チャンネル）か、`rooms.conf` の識別子（常設の部屋）。 */
      room: string;
      name: string;
      /**
       * **畳まれたチャンネルに入り直すときの表示名**（ADR-0022 §1-4）。
       *
       * 無人 5 分で畳まれると表示名も消えるので、覚えているものを添えて復活させる
       * （**先に入った人の名前になる** = 同 §6 のリスク 1）。
       */
      room_display_name?: string;
      /** ブラウザ側のバージョン（ADR-0012 のクライアントログ。入室時に 1 回）。 */
      app_version?: string;
      /** 回線の種別（粗い 3 区分）。 */
      net_kind?: "wifi" | "cellular" | "other";
      /** 何回目の張り直しか（初回は 0）。サーバのログで前後を結びつける手掛かり。 */
      rejoin_count?: number;
    }
  | { type: "leave" }
  /**
   * 部屋の一覧を求める。**入室前に送れる。**
   *
   * `group` を省くと**常設の部屋だけ**、入れると**そのグループのチャンネルだけ**返る
   * （ADR-0019 §1-9）。**省略可能な項目なのでバージョンは上がらない。**
   */
  | {
      type: "list_rooms";
      group?: string;
      /**
       * **このグループを見張ってほしい**（ADR-0023 §1-4）。
       *
       * 立てておくと、**チャンネルの作成・畳み・入室・退室のたびにサーバが `rooms` を押し出す**
       * （束ねる幅は 0.5 秒）。**見張るのは 1 セッション 1 グループだけ**で、別のグループで
       * 送り直すと差し替わる（同 §5 の未決 2 の決着）。
       *
       * **省略可能な項目なのでバージョンは上がらない**（省いたときのバイト列は足す前と同じ）。
       */
      watch?: boolean;
    }
  | { type: "set_mix_gain"; target: number; gain_db: number }
  /**
   * **チャンネルを作る**（ADR-0022 §1-3）。**入室前に送れる。**
   *
   * 利用者が決めるのは**表示名だけ**で、識別子はサーバが採番して `channel_created` で返す。
   * 入室は作る役目を兼ねない（畳まれたチャンネルへの入り直しだけは入室要求が作る）。
   */
  | {
      type: "create_channel";
      /** どのグループに作るか。**`new_group` を立てるときは空にする**（サーバが採番する）。 */
      group: string;
      display_name: string;
      /**
       * **新しいグループを作る**（ADR-0024・2026-10-21 のユーザー判断）。
       *
       * 立てると、サーバが**グループのコードを採番**（ランダムな Base32 10 文字。ADR-0021 §1-1）し、
       * **そのグループと最初のチャンネルを 1 往復で作る**。`channel_created` の `room`
       * （`グループ/管理ID`）にコードが入って返るので、画面はそれを所属として覚える。
       *
       * **グループの表示名は `display_name` をそのまま使う**（利用者に 2 つ名前を考えさせない）。
       * **省略可能な項目なのでバージョンは上がらない**（種別を増やさない。ADR-0019 §5-2）。
       */
      new_group?: boolean;
    };

/**
 * サーバが断る理由。**種別を増やすと古い実装は復号に失敗する**ので、
 * 増やせるのは**その版を配る前まで**（`too_many_groups` / `too_many_channels` は
 * バージョン 5 を配る前に相乗りさせた。2026-10-21 のユーザー判断）。
 */
export type ErrorCode =
  | "unsupported_version"
  | "invalid_request"
  /** チャンネルが満員（定員は既定 16 人）。 */
  | "room_full"
  /** **グループの数が上限に達した**（全体で 50。ADR-0025）。 */
  | "too_many_groups"
  /** **チャンネルの数が上限に達した**（全体で 100。ADR-0019 §1-5）。 */
  | "too_many_channels"
  | "internal";

export type ServerMessage =
  | {
      type: "joined";
      participant_id: number;
      topology: Topology;
      audio_profile: AudioProfile;
      roster: Participant[];
    }
  /**
   * 部屋の一覧。**要求への応答と押し出し（ADR-0023）で同じ形**なので区別しない。
   *
   * `group` と `group_display_name` は**どちらも省略可能**（バージョンは 5 のまま）。
   * **グループの表示名はこれでしか届かない** —— 招待 URL にはコードしか入っていない
   * （ADR-0021）ので、招待された端末はこれが無いとコードを出すしかない。
   * `group` が付いているのは、**押し出しが遅れて届くあいだにグループを切り替えたとき**に
   * 名前を取り違えて覚えないため。
   */
  | { type: "rooms"; rooms: RoomInfo[]; group?: string; group_display_name?: string }
  /**
   * **チャンネルを作った**（`create_channel` への応答。ADR-0022）。
   *
   * `room` は `グループ/管理ID` で、**そのまま入室要求と招待 URL に使える**。
   */
  | { type: "channel_created"; room: string; display_name: string }
  /** 名簿の差分。`changed` は**すでに居る人の中身が変わった**とき（S1 のミュート）。 */
  | { type: "roster_update"; joined?: Participant[]; left?: number[]; changed?: Participant[] }
  /**
   * **サーバから見た「自分の上り」の状態** (#60)。入室後に定期的に届く (既定 2 秒ごと)。
   *
   * 受信側は欠落率を測っているが**送信側へ伝える経路が無い**ので、サーバが測っている値を
   * そのまま返してもらう。使うのは想定欠落率の適応 (#61) とビットレート適応 (#62)。
   *
   * **値は整数だけ。** この CBOR 復号器は浮動小数点を扱わないので、送る側で単位を選んである
   * (区間は ms、遅延変動は µs)。
   */
  | {
      type: "uplink_report";
      /** 区間の長さ (ms)。**率を再現するのに必要**（#55 と同じ理由）。 */
      millis: number;
      /** この区間でサーバに届いたパケット数。 */
      received: number;
      /** この区間で落ちた数。 */
      lost: number;
      /** 遅延変動 (マイクロ秒)。 */
      jitter_us: number;
    }
  /**
   * **いま喋っている人の一覧**（2026-10-21・研究側の `890b5a7`。正本は DOC-0037）。
   *
   * **MCU では受信側で分からない**（届くのは合成音声 1 本だけ）ので、送り手ごとの発話の旗を
   * 見ているサーバが配る。**毎回「全員」が入る**（差分ではない）ので、受け手は手元の集合を
   * 丸ごと置き換える。**自分も入る。集合が変わったときだけ届く**（喋り終わりの余韻は 300 ms）。
   *
   * **`ids` が空は「誰も喋っていない」**（「情報が無い」ではない）。項目ごと省かれても空として読む。
   *
   * **種別が増えるが版は上がらない**（版 5 が未リリースのうちに相乗りしたため）。
   * 画面の使い方は `client/画面仕様.md` §3。
   */
  | { type: "speaking"; ids: number[] }
  | { type: "error"; code: ErrorCode; message: string };

/** メッセージ 1 つを「長さ前置き + CBOR」のバイト列にする。 */
export function encodeMessage(message: ClientMessage): Uint8Array {
  const body = cborEncode(message as unknown as CborValue);
  if (body.length > MAX_MESSAGE_LEN) {
    throw new RangeError(`制御メッセージが長すぎる: ${body.length} バイト`);
  }
  const out = new Uint8Array(LENGTH_PREFIX_LEN + body.length);
  new DataView(out.buffer).setUint32(0, body.length, false);
  out.set(body, LENGTH_PREFIX_LEN);
  return out;
}

function asMap(v: CborValue, what: string): Record<string, CborValue> {
  if (typeof v !== "object" || v === null || Array.isArray(v)) throw new TypeError(`${what} は map`);
  return v as Record<string, CborValue>;
}

function asAudioProfile(v: CborValue): AudioProfile {
  const m = asMap(v, "audio_profile");
  const codec = m["codec"];
  if (codec !== "opus") throw new TypeError("audio_profile.codec");
  const num = (key: string): number => {
    const x = m[key];
    if (typeof x !== "number") throw new TypeError(`audio_profile.${key}`);
    return x;
  };
  const flag = (key: string): boolean => {
    const x = m[key];
    if (typeof x !== "boolean") throw new TypeError(`audio_profile.${key}`);
    return x;
  };
  return {
    codec,
    sample_rate_hz: num("sample_rate_hz"),
    frame_ms: num("frame_ms"),
    bitrate_bps: num("bitrate_bps"),
    vbr: flag("vbr"),
    dtx: flag("dtx"),
    fec: flag("fec"),
    jitter_ms: num("jitter_ms"),
  };
}

function asRoomInfo(v: CborValue): RoomInfo {
  const m = asMap(v, "room");
  const id = m["id"];
  const display_name = m["display_name"];
  const topology = m["topology"];
  const participants = m["participants"];
  const max_participants = m["max_participants"];
  if (typeof id !== "string") throw new TypeError("room.id");
  if (typeof display_name !== "string") throw new TypeError("room.display_name");
  if (topology !== "sfu" && topology !== "mcu") throw new TypeError("room.topology");
  if (typeof participants !== "number") throw new TypeError("room.participants");
  if (typeof max_participants !== "number") throw new TypeError("room.max_participants");
  const info: RoomInfo = {
    id,
    display_name,
    topology,
    audio_profile: asAudioProfile(m["audio_profile"] as CborValue),
    participants,
    max_participants,
  };
  // **在室者の名前は省略可能**（バージョン 5 のサーバから届く）。中身が文字列でなければ無視する
  const names = m["participant_names"];
  if (Array.isArray(names)) {
    info.participant_names = names.filter((x): x is string => typeof x === "string");
  }
  return info;
}

function asParticipant(v: CborValue): Participant {
  if (typeof v !== "object" || v === null || Array.isArray(v)) throw new TypeError("participant は map");
  const id = v["id"];
  const name = v["name"];
  if (typeof id !== "number" || typeof name !== "string") throw new TypeError("participant の形が違う");
  const muted = v["muted"];
  // **申告が無い**（古い実装・まだ音声が届いていない）ときは項目ごと来ないので、そのまま省く
  return typeof muted === "boolean" ? { id, name, muted } : { id, name };
}

/** 復号した CBOR の値をサーバメッセージとして検証する。形が違えば例外。 */
export function parseServerMessage(v: CborValue): ServerMessage {
  if (typeof v !== "object" || v === null || Array.isArray(v)) throw new TypeError("メッセージは map");
  switch (v["type"]) {
    case "joined": {
      const participant_id = v["participant_id"];
      const topology = v["topology"];
      const roster = v["roster"];
      if (typeof participant_id !== "number") throw new TypeError("joined.participant_id");
      if (topology !== "sfu" && topology !== "mcu") throw new TypeError("joined.topology");
      if (!Array.isArray(roster)) throw new TypeError("joined.roster");
      return {
        type: "joined",
        participant_id,
        topology,
        audio_profile: asAudioProfile(v["audio_profile"] as CborValue),
        roster: roster.map(asParticipant),
      };
    }
    case "rooms": {
      const rooms = v["rooms"];
      if (!Array.isArray(rooms)) throw new TypeError("rooms.rooms");
      const out: ServerMessage = { type: "rooms", rooms: rooms.map(asRoomInfo) };
      // **省略可能な 2 つ。** 古いサーバからは届かないので、型が違えば黙って無視する
      // （在室者の名前と同じ扱い）
      const group = v["group"];
      const name = v["group_display_name"];
      if (typeof group === "string") out.group = group;
      if (typeof name === "string") out.group_display_name = name;
      return out;
    }
    case "roster_update": {
      const joined = v["joined"];
      const left = v["left"];
      const out: ServerMessage = { type: "roster_update" };
      if (joined !== undefined) {
        if (!Array.isArray(joined)) throw new TypeError("roster_update.joined");
        out.joined = joined.map(asParticipant);
      }
      if (left !== undefined) {
        if (!Array.isArray(left) || !left.every((x) => typeof x === "number")) {
          throw new TypeError("roster_update.left");
        }
        out.left = left as number[];
      }
      const changed = v["changed"];
      if (changed !== undefined) {
        if (!Array.isArray(changed)) throw new TypeError("roster_update.changed");
        out.changed = changed.map(asParticipant);
      }
      return out;
    }
    case "uplink_report": {
      const num = (key: string): number => {
        const x = v[key];
        if (typeof x !== "number") throw new TypeError(`uplink_report.${key}`);
        return x;
      };
      return {
        type: "uplink_report",
        millis: num("millis"),
        received: num("received"),
        lost: num("lost"),
        jitter_us: num("jitter_us"),
      };
    }
    case "channel_created": {
      const room = v["room"];
      const display_name = v["display_name"];
      if (typeof room !== "string" || typeof display_name !== "string") {
        throw new TypeError("channel_created");
      }
      return { type: "channel_created", room, display_name };
    }
    case "speaking": {
      const ids = v["ids"];
      // 項目が無ければ空として読む（保険。サーバは常に入れる）
      if (ids === undefined) return { type: "speaking", ids: [] };
      if (!Array.isArray(ids) || !ids.every((x) => typeof x === "number")) {
        throw new TypeError("speaking.ids");
      }
      return { type: "speaking", ids: ids as number[] };
    }
    case "error": {
      const code = v["code"];
      const message = v["message"];
      if (typeof code !== "string" || typeof message !== "string") throw new TypeError("error");
      return { type: "error", code: code as ErrorCode, message };
    }
    default:
      throw new TypeError(`不明なメッセージ種別: ${String(v["type"])}`);
  }
}

/**
 * ストリームから届いたバイト列を順に流し込み、完成したメッセージだけを取り出す。
 * ストリームは境界を保存しないので、1 メッセージが分割されて届いても、複数がまとめて届いてもよい。
 */
export class Decoder {
  private buf = new Uint8Array(0);

  push(bytes: Uint8Array): void {
    const next = new Uint8Array(this.buf.length + bytes.length);
    next.set(this.buf, 0);
    next.set(bytes, this.buf.length);
    this.buf = next;
  }

  /** 完成したメッセージがあれば 1 つ返す。無ければ null。形が違えば例外。 */
  next(): ServerMessage | null {
    if (this.buf.length < LENGTH_PREFIX_LEN) return null;
    const len = new DataView(this.buf.buffer, this.buf.byteOffset).getUint32(0, false);
    if (len > MAX_MESSAGE_LEN) {
      throw new RangeError(`制御メッセージが長すぎる: ${len} バイト (上限 ${MAX_MESSAGE_LEN})`);
    }
    if (this.buf.length < LENGTH_PREFIX_LEN + len) return null;
    const body = this.buf.slice(LENGTH_PREFIX_LEN, LENGTH_PREFIX_LEN + len);
    this.buf = this.buf.slice(LENGTH_PREFIX_LEN + len);
    return parseServerMessage(cborDecode(body));
  }

  get pendingLength(): number {
    return this.buf.length;
  }
}
